import { useEffect, useState } from "react";

/**
 * XREA側で用意されたInstagram Feed APIのエンドポイント。
 * アクセストークン等の秘密情報はサーバー(PHP)側にのみ存在し、
 * フロントエンドからはこの公開エンドポイントだけを呼び出す。
 */
const INSTAGRAM_FEED_ENDPOINT = "/api/instagram-feed.php";

export type InstagramFeedItem = {
  id: string;
  media_type?: string;
  image_url: string;
  permalink: string;
  timestamp?: string;
};

type InstagramFeedResponse = {
  ok?: boolean;
  count?: number;
  cache?: string;
  data?: InstagramFeedItem[];
};

type FeedStatus = "loading" | "ready" | "error";

type UseInstagramFeedResult = {
  items: InstagramFeedItem[];
  status: FeedStatus;
};

const isValidItem = (item: InstagramFeedItem): boolean =>
  Boolean(item && typeof item.image_url === "string" && item.image_url && typeof item.permalink === "string" && item.permalink);

export function useInstagramFeed(limit = 6): UseInstagramFeedResult {
  const [items, setItems] = useState<InstagramFeedItem[]>([]);
  const [status, setStatus] = useState<FeedStatus>("loading");

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const response = await fetch(INSTAGRAM_FEED_ENDPOINT, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("failed to load instagram feed");
        }

        const payload = (await response.json()) as InstagramFeedResponse;
        const list = Array.isArray(payload.data) ? payload.data.filter(isValidItem) : [];

        if (payload.ok === false || list.length === 0) {
          // ダミー代替はせず、空状態として扱う
          setItems([]);
          setStatus("ready");
          return;
        }

        setItems(list.slice(0, limit));
        setStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        // エラー内容はユーザーへ表示せず、グリッドのみ非表示にする
        setItems([]);
        setStatus("error");
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [limit]);

  return { items, status };
}